import { EntranceTypes } from "../helpers/helpers";

export const viewAfterSubmitting = (entranceExperience) => {
  return entranceExperience === EntranceTypes.AUTH0_ONLY ? "signin" : "one_time_login";
};

export const viewAfterSurvey = (entranceExperience) => {
  return entranceExperience === EntranceTypes.AUTH0_ONLY ? "submitting" : "password";
};

export const viewAfterWelcome = (badgesExist, surveyQuestionsExist, entranceExperience) => {
  if (!badgesExist && !surveyQuestionsExist) {
    return viewAfterSurvey(entranceExperience);
  }
  return badgesExist ? "badges" : "survey";
};

export const viewBeforePassword = (badgesExist, surveyQuestionsExist) => {
  if (!badgesExist && !surveyQuestionsExist) {
    return "welcome";
  }
  return surveyQuestionsExist ? "survey" : "badges";
};

/**
 * Generates an object representing the flow of views in a form based on the existence of badges, survey questions, and entrance experience.
 *
 * @param {boolean} badgesExist - Indicates if badges exist.
 * @param {boolean} surveyQuestionsExist - Indicates if survey questions exist.
 * @param {string} entranceExperience - The type of entrance experience.
 * @returns {Object} An object representing the flow of views.
 */
export const getAllViews = (badgesExist, surveyQuestionsExist, entranceExperience) => {
  return {
    welcome: {
      view: "welcome",
      next: viewAfterWelcome(badgesExist, surveyQuestionsExist, entranceExperience)
    },
    badges: {
      view: "badges",
      previous: "welcome",
      next: surveyQuestionsExist ? "survey" : viewAfterSurvey(entranceExperience)
    },
    survey: {
      view: "survey",
      next: viewAfterSurvey(entranceExperience),
      previous: badgesExist ? "badges" : "welcome"
    },
    password: {
      view: "password",
      previous: viewBeforePassword(badgesExist, surveyQuestionsExist),
      next: "one_time_login"
    },
    signin: { view: "signin", next: "submitting" },
    submitting: { view: "submitting", next: viewAfterSubmitting(entranceExperience) },
    wrong_info: { view: "wrong_info", previous: "welcome" },
    one_time_login: { view: "one_time_login" },
    login_issues__signin: { view: "login_issues__signin", previous: "signin" },
    login_issues__one_time_login: {
      view: "login_issues__one_time_login",
      previous: "one_time_login"
    }
  };
};

/**
 * Calculates the total number of pages based on the entrance type, badge presence, and survey questions.
 *
 * This function determines the base page count from the number of survey questions, adding one if badges exist,
 * and then adjusts the total based on the type of user experience:
 * - For NO_ACCOUNTS, it adds 4 additional pages (welcome, password, sign in, one time login).
 * - For AUTH0_ONLY, it adds 3 additional pages (welcome, sign in, one time login).
 * - For BOTH_ACCOUNTS, it sets the total experience pages to 2, as it bypasses the welcome and other extra pages.
 *
 * Additionally, it increments the number of question pages by 1 to account for an extra welcome page.
 *
 * @param {string} entranceExperience - The type of entrance experience (e.g., EntranceTypes.NO_ACCOUNTS, EntranceTypes.AUTH0_ONLY, EntranceTypes.BOTH_ACCOUNTS).
 * @param {boolean} badgesExist - A flag indicating whether badges exist.
 * @param {number} numSurveyQuestions - The number of survey questions.
 * @returns {{ numQuestionPages: number, numTotalExperiencePages: number }} An object containing:
 *   - numQuestionPages: The total number of pages for survey questions including the welcome page.
 *   - numTotalExperiencePages: The overall total number of pages in the flow.
 */
export const getTotalPages = (entranceExperience, badgesExist, numSurveyQuestions) => {
  const BASE_COUNT = badgesExist ? numSurveyQuestions + 1 : numSurveyQuestions;
  let numTotalExperiencePages = BASE_COUNT;

  switch (entranceExperience) {
    case EntranceTypes.NO_ACCOUNTS: {
      const numOtherPages = 3; // welcome, password, one time login
      numTotalExperiencePages = numTotalExperiencePages + numOtherPages;
      break;
    }
    case EntranceTypes.AUTH0_ONLY: {
      const numOtherPages = 3; // welcome, sign in, one time login
      numTotalExperiencePages = numTotalExperiencePages + numOtherPages;
      break;
    }
    case EntranceTypes.BOTH_ACCOUNTS:
      // this experience does not care about the welcome page or
      // any other question page as it goes straight to the sign in page always
      numTotalExperiencePages = 2;
      break;
    default:
      break;
  }
  const numQuestionPages = BASE_COUNT + 1; // account for welcome page
  return { numQuestionPages, numTotalExperiencePages };
};

export const getCurrentQuestion = (badgesLength, surveyQuestions, currentPage) => {
  let surveyQuestionIndex = currentPage - 2; // account for welcome page and zero-based index

  if (badgesLength > 0) {
    surveyQuestionIndex -= 1; // account for badges page
  }

  const currentQuestion = surveyQuestions.at(surveyQuestionIndex);

  return {
    currentQuestion,
    isEnd: surveyQuestionIndex === surveyQuestions.length - 1
  };
};
